import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import '../styles/DraftModal.css';
import ThreadHistory from './ThreadHistory';
import ContactProfile from './ContactProfile';
import DraftEditor from './DraftEditor';

function DraftModal({ draft, onClose, onRefresh }) {
  const [fullDraft, setFullDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendStatus, setSendStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('draft');

  // Fetch full draft details
  useEffect(() => {
    fetchFullDraft();
  }, [draft.id]);

  const fetchFullDraft = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/drafts/${draft.id}`);
      setFullDraft(response.data);
      setEditBody(response.data.draft.body);
      setError(null);
    } catch (err) {
      setError(`Failed to load draft details: ${err.message}`);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!fullDraft.contact.channels?.email?.[0]) {
      setError('No email address found for this contact');
      return;
    }

    setSendLoading(true);
    setSendStatus('Sending...');

    try {
      const response = await axios.post(`/api/drafts/${draft.id}/send-email`, {
        to_address: fullDraft.contact.channels.email[0],
        subject: `Re: ${fullDraft.thread.subject || 'Message'}`,
        body_override: editBody,
      });

      setSendStatus('✓ Email sent successfully!');
      setTimeout(() => {
        onClose();
        onRefresh();
      }, 1500);
    } catch (err) {
      setError(`Failed to send email: ${err.response?.data?.error || err.message}`);
      setSendStatus(null);
    } finally {
      setSendLoading(false);
    }
  };

  const handlePlaywrightInsert = async () => {
    setSendLoading(true);
    setSendStatus('Opening compose...');

    try {
      const response = await axios.post(`/api/drafts/${draft.id}/playwright-insert`, {
        channel: fullDraft.draft.channel,
      });

      setSendStatus('✓ Compose opened. Complete the send manually.');
      setTimeout(() => {
        onClose();
        onRefresh();
      }, 2000);
    } catch (err) {
      setError(`Failed to open compose: ${err.response?.data?.error || err.message}`);
      setSendStatus(null);
    } finally {
      setSendLoading(false);
    }
  };

  const handleDiscard = async () => {
    if (!window.confirm('Are you sure? This action is permanent and cannot be undone.')) {
      return;
    }

    setSendLoading(true);
    setSendStatus('Discarding...');

    try {
      await axios.post(`/api/drafts/${draft.id}/discard`, {
        reason: 'Discarded by Boris',
      });

      setSendStatus('✓ Draft discarded');
      setTimeout(() => {
        onClose();
        onRefresh();
      }, 1000);
    } catch (err) {
      setError(`Failed to discard draft: ${err.response?.data?.error || err.message}`);
      setSendStatus(null);
    } finally {
      setSendLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setSendLoading(true);
      await axios.post(`/api/drafts/${draft.id}/edit`, {
        body: editBody,
      });
      setIsEditing(false);
      fetchFullDraft();
    } catch (err) {
      setError(`Failed to save draft: ${err.message}`);
    } finally {
      setSendLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="loading-state">Loading draft details...</div>
        </div>
      </div>
    );
  }

  if (!fullDraft) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <div className="error-state">Failed to load draft</div>
        </div>
      </div>
    );
  }

  const isDraft = fullDraft.draft.state === 'pending';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h2>Review Draft</h2>
            <span className="channel-badge">
              {fullDraft.draft.channel}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {error && (
          <div className="modal-error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>✕</button>
          </div>
        )}

        <div className="modal-tabs">
          <button
            className={`tab ${activeTab === 'draft' ? 'active' : ''}`}
            onClick={() => setActiveTab('draft')}
          >
            Draft
          </button>
          <button
            className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            History
          </button>
          <button
            className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            Contact
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'draft' && (
            <div className="tab-pane">
              <div className="draft-section">
                <h3>Draft Message</h3>
                {isEditing ? (
                  <DraftEditor
                    body={editBody}
                    onBodyChange={setEditBody}
                    onSave={handleSaveEdit}
                    onCancel={() => setIsEditing(false)}
                    isLoading={sendLoading}
                  />
                ) : (
                  <div className="draft-body">
                    <pre>{fullDraft.draft.body}</pre>
                  </div>
                )}
              </div>

              {fullDraft.draft.new_move && (
                <div className="new-move-section">
                  <h4>What's New</h4>
                  <p>{fullDraft.draft.new_move}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <ThreadHistory messages={fullDraft.messages} />
          )}

          {activeTab === 'contact' && (
            <ContactProfile contact={fullDraft.contact} />
          )}
        </div>

        {isDraft && (
          <div className="modal-footer">
            <div className="action-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => setIsEditing(!isEditing)}
                disabled={sendLoading}
              >
                ✎ {isEditing ? 'Cancel Edit' : 'Edit'}
              </button>

              {fullDraft.draft.channel === 'email' ? (
                <button
                  className="btn btn-primary"
                  onClick={handleSendEmail}
                  disabled={sendLoading || isEditing}
                >
                  {sendLoading ? 'Sending...' : '✓ Send Email'}
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handlePlaywrightInsert}
                  disabled={sendLoading || isEditing}
                >
                  {sendLoading ? 'Opening...' : '✓ Edit in ' + fullDraft.draft.channel}
                </button>
              )}

              <button
                className="btn btn-danger"
                onClick={handleDiscard}
                disabled={sendLoading}
              >
                🗑️ Discard
              </button>
            </div>

            {sendStatus && (
              <div className="send-status">{sendStatus}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DraftModal;
